import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";

import Icon from "../general/icon";
import TextField from "../general/form/textField";

import { general } from "../../../store/action";
import {
  isShowModalDataSelector,
  modalDataSelector,
  modalNameSelector,
} from "../../../store/selector/general";

import { SakhadVerifyCaptchaType } from "../../../types/sakhadAuth";

import {
  useSakhadGetCaptcha,
  useSakhadVerifyCaptcha,
} from "../../../hook/request/providerSakhad";

import { resolveCaptchaQueue, rejectCaptchaQueue } from "../../../services/api";

type Props = {
  modalData: any;
  modalName: string;
  isShowModalData: boolean;
  handleHideModal: () => void;
};

const SakhadVerifyCaptchaModal = ({
  modalData,
  modalName,
  isShowModalData,
  handleHideModal,
}: Props) => {
  const [showState, setShowState] = useState(false);
  const [captcha, setCaptcha] = useState<string>("");
  const [captchaSessionId, setCaptchaSessionId] = useState<string>("");
  const [loadingImage, setLoadingImage] = useState(false);

  const getSakhadCaptcha = useSakhadGetCaptcha();
  const verifyCaptchaResponse = useSakhadVerifyCaptcha();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SakhadVerifyCaptchaType>({});

  const setCaptchaImage = (captchaValue: string) => {
    setCaptcha(`data:image/png;base64,${captchaValue}`);
  };

  // Open modal and set first captcha
  useEffect(() => {
    if (isShowModalData && modalName === "sakhadVerifyCaptchaModal") {
      setShowState(true);

      if (modalData?.data?.captcha) {
        setCaptchaImage(modalData.data.captcha);
      }

      if (modalData?.data?.sessionId) {
        setCaptchaSessionId(modalData.data.sessionId);
      }
    }
  }, [modalData, modalName, isShowModalData]);

  const handleClose = () => {
    setShowState(false);

    handleHideModal();

    rejectCaptchaQueue();

    reset();

    setCaptcha("");

    setCaptchaSessionId("");
  };

  const refreshCaptcha = async () => {
    setLoadingImage(true);

    try {
      const response = await getSakhadCaptcha.mutateAsync(undefined);

      const captchaData = response?.data?.result;

      if (captchaData?.captcha) {
        setCaptchaImage(captchaData.captcha);

        setCaptchaSessionId(captchaData.sessionId);

        reset();
      }
    } catch (error) {
      console.error("SAKHAD_REFRESH_ERROR", error);
    } finally {
      setLoadingImage(false);
    }
  };

  const handleSubmitCode: SubmitHandler<SakhadVerifyCaptchaType> = (data) => {
    verifyCaptchaResponse.mutate(
      {
        answer: data.answer,

        // If your backend needs it:
        // sessionId: captchaSessionId
      },

      {
        onSuccess: () => {
          resolveCaptchaQueue(data.answer);

          setShowState(false);

          handleHideModal();

          reset();
        },

        onError: () => {
          // keep modal open
          // user can retry
        },
      },
    );
  };

  return (
    <Modal show={showState} onHide={handleClose} centered bodyClassName="p-0">
      <div className="captcha-modal-content w-100 mw-100 mx-auto py-3">
        <div className="d-flex align-items-center justify-content-between mb-4 fs-3 px-4">
          <h4 className="title-dot font-16 font-weight-bold mb-0">
            تایید کد کپچا
          </h4>

          <button
            type="button"
            className="p-0 border-0 bg-transparent"
            onClick={handleClose}
          >
            <Icon name="close-square" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleSubmitCode)}>
          <div className="px-4">
            <div className="form-group mb-4">
              <label className="inp-lbl me-2 mb-2">
                به علت فراخوانی بیش از حد مجاز سرویس‌ها، لطفاً کد کپچا زیر را
                وارد نمایید.
              </label>

              <div className="captcha-image-wrapper mb-3 text-center">
                {loadingImage ? (
                  <div>در حال بارگذاری...</div>
                ) : captcha ? (
                  <img src={captcha} alt="Captcha" className="border rounded" />
                ) : null}

                <button
                  type="button"
                  className="m-1 border-0 bg-transparent"
                  onClick={refreshCaptcha}
                >
                  <Icon name="refresh" />
                </button>
              </div>

              <TextField
                name="answer"
                id="captcha-answer"
                placeholder="کد کپچا"
                control={control}
                error={errors}
                className="ltr text-center"
              />
            </div>
          </div>

          <div className="d-flex px-4 gap-3">
            <button
              type="submit"
              className="btn btn-primary ms-auto"
              disabled={verifyCaptchaResponse.isPending}
            >
              {verifyCaptchaResponse.isPending ? "در حال ارسال..." : "ارسال"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  modalData: modalDataSelector(state),

  modalName: modalNameSelector(state),

  isShowModalData: isShowModalDataSelector(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  handleHideModal: () => dispatch(general.handleHideModal()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SakhadVerifyCaptchaModal);
